import _omit from "lodash/omit";
import { SUPPRESSION_TYPES } from '@kentik/ui-shared/alerting/constants';
import api from 'core/util/api';
import { buildKeyValueFilters, replaceEditableKeyValueFilters } from 'app/forms/config/suppressions/suppression';
import { contextFilterFromFormValues, contextFilterToFormValues, NETWORK_SCOPE_ANY } from 'app/util/contextFilters';
import { deserializeSuppressionApplications, serializeSuppressionApplications } from './suppressionApplications';
import { updateReferenceKeyFilter } from './suppressionReferences';
import SuppressionCollection from './SuppressionCollection';
export class SuppressionsStore {
  constructor() {
    this.store = void 0;
    this.collection = void 0;
  }
  initialize() {
    this.collection = new SuppressionCollection([], {
      store: this.store
    });
    return Promise.resolve();
  }
  previewSuppression(request) {
    return api.post('/api/ui/alertingManager/suppressions/preview', {
      data: request
    });
  }
  syncModel(model, {
    addIfMissing = false
  } = {}) {
    const cached = this.collection.get(model.id);
    if (cached && cached !== model) {
      cached.set(model.get());
      this.collection.filter();
    } else if (!cached && addIfMissing) {
      const [added] = this.collection.add([model.get()]);
      this.collection.resetFetchCache();
      return added;
    }
    this.collection.resetFetchCache();
    return cached || model;
  }
  convertModelDataToFormData(model, $metrics) {
    const {
      type,
      measurement,
      rawFilters,
      comment,
      rawAgentIds,
      rawTestIds,
      rule_ids
    } = model;
    const uiProperties = model.get('uiProperties');
    const applicationMetadata = uiProperties?.applicationMetadata || {};
    const applications = deserializeSuppressionApplications(model.get('applications'));
    const conditionsGroup = [{
      connector: model.get('key_filter')?.strict ?? model.get('strict') ? 'full' : 'partial',
      filters: []
    }];
    const initValues = {
      type,
      display_name: model.get('display_name'),
      comment,
      rule_ids,
      severities: model.get('severities'),
      applications,
      context_filter: contextFilterToFormValues(model.get('context_filter')),
      start_time: model.get('start_time') ?? undefined,
      end_time: model.get('end_time') ?? undefined,
      neverExpire: !model.get('end_time'),
      uiProperties,
      conditionsGroup
    };
    switch (type) {
      case SUPPRESSION_TYPES.SYNTH:
        initValues.applications = [];
        initValues.testIds = rawTestIds;
        break;
      case SUPPRESSION_TYPES.AGENT:
        initValues.applications = [];
        initValues.agentIds = rawAgentIds;
        break;
      case SUPPRESSION_TYPES.NMS:
        initValues.measurement = applicationMetadata.measurement;
        conditionsGroup[0].filters = model.rawFilters;
        break;
      case SUPPRESSION_TYPES.CONDITION:
        conditionsGroup[0].filters = model.rawFilters;
        break;
      case SUPPRESSION_TYPES.METRIC:
        conditionsGroup[0].filters = $metrics.dimensionFilterKdeToReconTransformer(rawFilters, measurement);
        break;
      default:
    }
    return initValues;
  }
  buildCloneDraft(model, $metrics) {
    const source = this.convertModelDataToFormData(model, $metrics);
    const {
      uiProperties
    } = source;
    const configuration = _omit(source, ['display_name', 'start_time', 'end_time', 'neverExpire']);
    let modelAttributes = _omit(model.get(), ['id', 'display_name', 'user_id', 'rule_id', 'rule_application', 'start_time', 'start_time_code', 'end_time', 'end_time_code', 'cdate', 'edate', 'tableKey', 'uiProperties']);
    if ([SUPPRESSION_TYPES.SYNTH, SUPPRESSION_TYPES.AGENT].includes(source.type)) {
      modelAttributes = _omit(modelAttributes, ['rule_ids', 'severities', 'applications']);
    }
    return {
      initialValues: {
        ...configuration,
        uiProperties: {
          applicationMetadata: uiProperties?.applicationMetadata,
          startAsSoonAsPossible: true
        }
      },
      modelAttributes: {
        ...modelAttributes,
        uiProperties: {
          applicationMetadata: uiProperties?.applicationMetadata,
          startAsSoonAsPossible: true
        }
      }
    };
  }
  buildContextFilterDraft({
    field,
    values,
    comment
  }, $metrics) {
    const applicationMetadata = {
      type: SUPPRESSION_TYPES.NONE
    };
    const predicate = values.length === 1 ? {
      field,
      operator: 'equals',
      not: false,
      value: values[0]
    } : {
      field,
      operator: 'in',
      not: false,
      value: values
    };
    const suppression = this.collection.build({
      comment,
      context_filter: contextFilterFromFormValues({
        conjunctions: [{
          clauses: [{
            network_scope: [NETWORK_SCOPE_ANY],
            predicates: [predicate]
          }]
        }]
      }),
      application_metadata: JSON.stringify(applicationMetadata),
      uiProperties: {
        startAsSoonAsPossible: true,
        applicationMetadata
      }
    }, {
      deserialize: false
    });
    return this.buildCloneDraft(suppression, $metrics);
  }
  convertFormDataToModelData(formValues, {
    $metrics,
    sourceModel
  }) {
    const {
      type,
      display_name,
      start_time,
      neverExpire,
      end_time,
      comment,
      rule_ids = [],
      severities,
      applications,
      context_filter,
      testIds,
      agentIds,
      measurement,
      uiProperties,
      conditionsGroup = []
    } = formValues;
    const {
      connector,
      filters = []
    } = conditionsGroup[0] || {};
    const existingReferenceFilter = sourceModel.type === type ? sourceModel.get('key_filter') : undefined;
    const applicationMetadata = {
      type
    };
    const strict = connector === 'full';
    const updateKeyFilter = editableFilters => replaceEditableKeyValueFilters(existingReferenceFilter, editableFilters, strict);
    const serializeOptionalArray = (values, existing) => {
      if (values?.length) {
        return values;
      }
      if (sourceModel.isNew || existing === undefined) {
        return undefined;
      }
      return values;
    };
    const existingRuleIds = sourceModel.get('rule_ids') ?? (sourceModel.get('rule_id') ? [sourceModel.get('rule_id')] : undefined);
    const modelData = {
      strict,
      display_name,
      start_time,
      end_time: neverExpire ? null : end_time,
      rule_ids: serializeOptionalArray(rule_ids, existingRuleIds),
      severities: serializeOptionalArray(severities, sourceModel.get('severities')),
      applications: serializeSuppressionApplications(serializeOptionalArray(applications, deserializeSuppressionApplications(sourceModel.get('applications'))), sourceModel.get('applications')),
      key_filter: {
        filters: []
      },
      // null clears; undefined would be dropped from the payload and leave the stored filter live.
      context_filter: contextFilterFromFormValues(context_filter) ?? null,
      comment,
      uiProperties
    };
    switch (type) {
      case SUPPRESSION_TYPES.SYNTH:
        modelData.strict = false;
        modelData.rule_ids = [];
        modelData.applications = [];
        modelData.key_filter = updateReferenceKeyFilter(existingReferenceFilter, 'testId', testIds || []);
        modelData.key_filter.strict = false;
        if (!testIds?.length) {
          applicationMetadata.type = SUPPRESSION_TYPES.CONDITION;
        }
        break;
      case SUPPRESSION_TYPES.AGENT:
        modelData.strict = false;
        modelData.rule_ids = [];
        modelData.applications = [];
        modelData.key_filter = updateReferenceKeyFilter(existingReferenceFilter, ['agentId', 'targetAgentId'], agentIds || []);
        modelData.key_filter.strict = false;
        if (!agentIds?.length) {
          applicationMetadata.type = SUPPRESSION_TYPES.CONDITION;
        }
        break;
      case SUPPRESSION_TYPES.NMS:
        applicationMetadata.measurement = measurement;
        modelData.key_filter = updateKeyFilter(buildKeyValueFilters(filters));
        break;
      case SUPPRESSION_TYPES.CONDITION:
        modelData.key_filter = updateKeyFilter(buildKeyValueFilters(filters));
        break;
      case SUPPRESSION_TYPES.METRIC:
        {
          // kmetrics suppressions store measurement as a dimension filter
          const measurementFilterValueCandidate = filters.find(({
            filterField
          }) => filterField.endsWith('km_measurement_name'))?.filterValue;
          const measurementFilterValue = typeof measurementFilterValueCandidate === 'string' ? measurementFilterValueCandidate : undefined;
          const transformedFilters = $metrics.dimensionFilterReconToKdeTransformer(filters, measurementFilterValue);
          applicationMetadata.measurement = measurementFilterValue;
          modelData.key_filter = updateKeyFilter(buildKeyValueFilters(transformedFilters));
          break;
        }
      default:
    }
    return {
      ...modelData,
      uiProperties: {
        ...uiProperties,
        applicationMetadata
      },
      application_metadata: JSON.stringify(applicationMetadata, null, 2)
    };
  }
}
export default new SuppressionsStore();